<template>
  <!-- type=1 HTML container：有 children 走遞迴 -->
  <div v-if="renderType === RenderTypeEnum.HTML && hasChildren">
    <content-data v-for="(child, idx) in children" :key="idx" :node="child" />
  </div>

  <!-- type=1 HTML leaf：inline render，讓相鄰文字 <br/> 正常 inline flow -->
  <div v-else-if="renderType === RenderTypeEnum.HTML" class="inline" v-html="meta"></div>

  <!-- type=2 img：leaf media -->
  <div v-else-if="renderType === RenderTypeEnum.MEDIA && isMediaError" class="aspect-[1.8] w-full h-full bg-gray-30 rounded"></div>
  <component :is="mediaTag" v-else-if="renderType === RenderTypeEnum.MEDIA" ref="mediaRef" v-bind="mediaAttrs" @error="handleMediaError" />

  <!-- type=3 youtube：抽成獨立子元件（效能考量：ContentData 為遞迴元件，若直接在此無條件呼叫
       useYoutubeResumablePlayer/useIntersectionObserver，樹狀結構中每一個非 YouTube 節點都會白白
       付出這些 composable 初始化的成本）——只有真的是 YOUTUBE 節點時才會掛載，才會建立播放器。 -->
  <client-only v-else-if="renderType === RenderTypeEnum.YOUTUBE">
    <content-youtube :video-id="meta" />
  </client-only>

  <!-- type=4 ul / type=5 ol：走 list_v2 遞迴 li（保留巢狀層級）。舊 flat attr.list 已 deprecated，前端不再讀 -->
  <component :is="listTag" v-else-if="renderType === RenderTypeEnum.LIST">
    <content-data v-for="(listItem, idx) in list" :key="idx" :node="listItem" />
  </component>

  <!-- type=6 li：內含 1/2/3/4/5，走遞迴 -->
  <li v-else-if="renderType === RenderTypeEnum.LIST_ITEM">
    <content-data v-for="(child, idx) in children" :key="idx" :node="child" />
  </li>
</template>

<script setup lang="ts">
// ref: niceday.web /components/common/content-data.vue
import { ProductDetailHtmlTypeEnum, type ProductContentData } from '@/types/product'
import ContentYoutube from '@/components/common/content-youtube.vue'

interface Props {
  /** 「方案介紹」/「購買須知」靜態內容的單一 tree node */
  node: ProductContentData
}

enum RenderTypeEnum {
  HTML,
  MEDIA,
  YOUTUBE,
  LIST,
  LIST_ITEM,
}

defineOptions({ name: 'ContentData' })

const props = defineProps<Props>()

const mediaRef = useTemplateRef<HTMLElement>('mediaRef')
const isMediaError = ref(false)

const type = computed(() => props.node.type)
const meta = computed(() => props.node.meta ?? '')

const children = computed(() => props.node.children ?? [])
const hasChildren = computed(() => children.value.length > 0)
const list = computed(() => props.node.attr?.list_v2 ?? [])
const listTag = computed(() => (type.value === ProductDetailHtmlTypeEnum.ORDERED_LIST ? 'ol' : 'ul'))

const mediaTag = computed(() => (type.value === ProductDetailHtmlTypeEnum.IMAGE ? 'img' : 'div'))

const renderType = computed(() => {
  switch (type.value) {
    case ProductDetailHtmlTypeEnum.IMAGE:
      return RenderTypeEnum.MEDIA
    case ProductDetailHtmlTypeEnum.YOUTUBE:
      return RenderTypeEnum.YOUTUBE
    case ProductDetailHtmlTypeEnum.UNORDERED_LIST:
    case ProductDetailHtmlTypeEnum.ORDERED_LIST:
      return RenderTypeEnum.LIST
    case ProductDetailHtmlTypeEnum.LIST_ITEM:
      return RenderTypeEnum.LIST_ITEM
    case ProductDetailHtmlTypeEnum.HTML:
    default:
      return RenderTypeEnum.HTML
  }
})

const mediaAttrs = computed(() => {
  const width = props.node.attr?.width ?? 0
  const height = props.node.attr?.height ?? 0

  return {
    src: meta.value,
    class: 'w-full h-full cursor-pointer rounded',
    style: { 'aspect-ratio': width && height ? `${width}/${height}` : undefined },
  }
})

// Figma spec（niceday 沒有）：圖片/影片載入失敗時顯示灰色佔位區塊取代破圖
const handleMediaError = () => {
  isMediaError.value = true
}
</script>

<style lang="scss" scoped>
p {
  @apply my-4;
}
ol {
  @apply list-decimal my-4;
  padding-inline-start: 2rem;
}
ul {
  @apply list-disc my-4;
  padding-inline-start: 2rem;
}
li {
  @apply my-2;
}

:deep(a) {
  @apply text-red-50 no-underline hover:underline;
}
</style>
